import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5323a215"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "shared-research-groups" }
const _hoisted_2 = {
  key: 0,
  class: "empty-list"
}
const _hoisted_3 = {
  key: 1,
  class: "help-text"
}
const _hoisted_4 = {
  key: 2,
  class: "list-unstyled"
}
const _hoisted_5 = { class: "item-actions" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = {
  key: 4,
  class: "list-actions"
}
const _hoisted_8 = {
  key: 2,
  class: "form-label"
}
const _hoisted_9 = {
  key: 2,
  class: "alert alert-primary alert-sm"
}
const _hoisted_10 = { class: "remove-research-group" }
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "Stop sharing this tree with the group?", -1))

import { computed, ref, watch } from 'vue'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useDataGroupMemberStore } from '@/gp/DataGroupMemberStore'
import { useDataGroupInviteStore } from '@/gp/DataGroupInviteStore'
import { InvitationResult, ItemPermissions, PrincipalType } from '@/gp/GroupAdminModel'
import { useWorkspaceStore } from '@/rd/WorkspaceStore'
import { AsyncData, LoadMode } from '@/util/AsyncData'
import { isDefined } from '@/util/TypeScriptUtil'
import { LockIcon, PlusIcon } from '@zhuowenli/vue-feather-icons'
import { VersionedKeyList } from '@/util/AsyncDataStore'
import { PendingStatus } from '@/util/AppUtil'
import DataGroupChooseShareModal from '@/manage/datagroups/DataGroupChooseShareModal.vue'
import DataGroupInviteModal from '@/manage/datagroups/DataGroupInviteModal.vue'
import ResearchGroupCard from '@/manage/researchgroups/ResearchGroupCard.vue'
import Modal from '@/util/Modal.vue'
import ResearchGroupItem from '../researchgroups/ResearchGroupItem.vue'
import LoadingIndicator from '@/util/LoadingIndicator.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'FamilyGroupSharedResearchGroups',
  props: {
    familyGroupId: {}
  },
  emits: ["chooseMembers"],
  setup(__props: any, { emit }) {

const props = __props;





const loadingRef = ref(false)
const asyncPermissionsRef = ref<AsyncData<ItemPermissions>>()
const asyncDataGroupMemberKeyListRef = ref<AsyncData<VersionedKeyList>>()
const asyncDataGroupInviteKeyListRef = ref<AsyncData<VersionedKeyList>>()
const showClosedRequestsRef = ref(false)
const removeDataGroupIdRef = ref<string>()

const dataGroupChooseModalRef = ref<InstanceType<typeof DataGroupChooseShareModal>>()
const dataGroupInviteModalRef = ref<InstanceType<typeof DataGroupInviteModal>>()
const removeResearchGroupModalRef = ref<InstanceType<typeof Modal>>()

const familyGroupStore = useFamilyGroupStore()
const dataGroupStore = useDataGroupStore()
const dataGroupMemberStore = useDataGroupMemberStore()
const dataGroupInviteStore = useDataGroupInviteStore()
const workspaceStore = useWorkspaceStore()

const permissionsRef = computed(() => asyncPermissionsRef.value?.data ?? ItemPermissions.None)
const isOwnerRef = computed(() => (permissionsRef.value & ItemPermissions.Owner) > 0)
const sharedDataGroupsRef = computed(() => {
  const memberKeys = asyncDataGroupMemberKeyListRef.value?.data?.keys ?? []
  const sharedDataGroupIds = dataGroupMemberStore.getLoadedMembers(memberKeys).map(m => m.dataGroupId!) // loaded with key list
  return dataGroupStore.getLoadedGroupList(sharedDataGroupIds) // loaded with members
})
const sharedResearchGroupsRef = computed(() => sharedDataGroupsRef.value.filter(dg => !dg.isProfile))
const sharedProfilesRef = computed(() => sharedDataGroupsRef.value.filter(dg => dg.isProfile))
const invitesRef = computed(() => {
  const inviteKeys = asyncDataGroupInviteKeyListRef.value?.data?.keys ?? []
  const invites = dataGroupInviteStore.getLoadedInvites(inviteKeys) // loaded with key list
  const inviteGroups = dataGroupStore.getLoadedGroupList(invites.map(n => n.dataGroupId))
  const groupIds = new Set(inviteGroups.filter(dg => dg && !dg?.isProfile).map(rg => rg.id!))
  return invites.filter(n => groupIds.has(n.dataGroupId!))
})
const showChooseMembersRef = computed(() =>
  isOwnerRef.value && sharedResearchGroupsRef.value.length && sharedProfilesRef.value.length < 2)

const asyncWorkspaceKeyListsRef = computed(() => {
  const groupIds = sharedResearchGroupsRef.value.map(rg => rg.id!)
  groupIds.push(...invitesRef.value.map(i => i.dataGroupId!))
  // HACK: method can't actually handle multiple groups, so call it once for each group
  return groupIds.flatMap(id => workspaceStore.getAsyncKeyListsForGroups([id], LoadMode.EnsureLoaded))
})

const canListItemsRef = computed(() => (permissionsRef.value ?? 0) & ItemPermissions.ListItems)
const canEditRef = computed(() => (permissionsRef.value ?? 0) & ItemPermissions.Modify)
const visibleInvitesRef = computed(() => 
  invitesRef.value.filter(inv => showClosedRequestsRef.value || inv.result == InvitationResult.Pending))
const hasClosedInvitesRef = computed(() => 
  invitesRef.value.some(inv => inv.result != InvitationResult.Pending))

watch(props, async () => {
  loadingRef.value = true
  try {
    asyncPermissionsRef.value = familyGroupStore.getAsyncPermissions(props.familyGroupId, LoadMode.EnsureLoaded)
    asyncDataGroupMemberKeyListRef.value = dataGroupMemberStore.getAsyncKeyListsForFamilyGroups([props.familyGroupId], LoadMode.EnsureLoaded).at(0)
    asyncDataGroupInviteKeyListRef.value = dataGroupInviteStore.getAsyncKeyListForFamilyGroup(props.familyGroupId, LoadMode.EnsureLoaded)

    await Promise.all([
      asyncPermissionsRef.value?.whenLoadCompleted,
      asyncDataGroupMemberKeyListRef.value?.whenLoadCompleted, 
      asyncDataGroupInviteKeyListRef.value?.whenLoadCompleted,
    ])

    await Promise.all(asyncWorkspaceKeyListsRef.value.map(a => a.whenLoadCompleted))
  }
  finally {
    loadingRef.value = false
  }
}, { immediate: true })

function onShareResearchGroup() {
  dataGroupChooseModalRef.value?.open(
    props.familyGroupId, 
    sharedResearchGroupsRef.value.map(rg => rg.id!))
}

function onRequestShare(dataGroupId: string) {
  dataGroupInviteModalRef.value?.openNew(dataGroupId, props.familyGroupId)
}

function onSelectDataGroupInvite(inviteId: string) {
  dataGroupInviteModalRef.value?.openEdit(inviteId)
}

function onRemoveResearchGroup(dataGroupId: string) {
  removeDataGroupIdRef.value = dataGroupId
  removeResearchGroupModalRef.value?.open()
}

async function removeResearchGroup() {
  const dm = await dataGroupMemberStore.getMemberByPrincipalAsync(removeDataGroupIdRef.value!, PrincipalType.FamilyGroup, props.familyGroupId)
  if (dm) {
    await dataGroupMemberStore.deleteAsync(dm)
  }

  removeResearchGroupModalRef.value?.close()
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (loadingRef.value)
      ? (_openBlock(), _createBlock(LoadingIndicator, { key: 0 }))
      : (canListItemsRef.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (!sharedResearchGroupsRef.value.length && !visibleInvitesRef.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, " No trees are shared with this group. "))
              : (_openBlock(), _createElementBlock("div", _hoisted_3, " Members of the group can see these trees. ")),
            (sharedResearchGroupsRef.value.length || visibleInvitesRef.value.length)
              ? (_openBlock(), _createElementBlock("ul", _hoisted_4, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleInvitesRef.value, (inv) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: inv.id
                    }, [
                      _createVNode(ResearchGroupCard, {
                        "data-group-id": inv.dataGroupId!,
                        small: "",
                        "select-mode": "",
                        status: _unref(PendingStatus),
                        onSelected: ($event: any) => (onSelectDataGroupInvite(inv.id!))
                      }, null, 8, ["data-group-id", "status", "onSelected"])
                    ]))
                  }), 128)),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sharedResearchGroupsRef.value, (rg) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: rg.id
                    }, [
                      _createVNode(ResearchGroupCard, {
                        "data-group-id": rg.id!,
                        small: ""
                      }, null, 8, ["data-group-id"]),
                      _createElementVNode("div", _hoisted_5, [
                        (canEditRef.value)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 0,
                              type: "button",
                              class: "btn btn-inline btn-link",
                              onClick: ($event: any) => (onRemoveResearchGroup(rg.id!))
                            }, " Remove ", 8, _hoisted_6))
                          : _createCommentVNode("", true)
                      ])
                    ]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            (showChooseMembersRef.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 3,
                  type: "button",
                  class: "btn btn-primary choose-members",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('chooseMembers')))
                }, " Choose group members "))
              : _createCommentVNode("", true),
            (canEditRef.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  (sharedResearchGroupsRef.value.length == 0)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        type: "button",
                        class: "btn btn-primary",
                        onClick: onShareResearchGroup
                      }, [
                        _createVNode(_unref(PlusIcon), { size: "1.2x" }),
                        _createTextVNode(" Share a tree ")
                      ]))
                    : (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        type: "button",
                        class: "btn btn-inline btn-link btn-sm",
                        onClick: onShareResearchGroup
                      }, " Share another tree ")),
                  (hasClosedInvitesRef.value)
                    ? (_openBlock(), _createElementBlock("label", _hoisted_8, [
                        _withDirectives(_createElementVNode("input", {
                          type: "checkbox",
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showClosedRequestsRef).value = $event)),
                          class: "form-check-input"
                        }, null, 512), [
                          [_vModelCheckbox, showClosedRequestsRef.value]
                        ]),
                        _createTextVNode("  Show approved and expired sharing requests ")
                      ]))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ], 64))
        : (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createVNode(_unref(LockIcon)),
            _createTextVNode(" You do not have permission to see the trees shared with this group. ")
          ])),
    _createVNode(DataGroupInviteModal, {
      ref_key: "dataGroupInviteModalRef",
      ref: dataGroupInviteModalRef
    }, null, 512),
    _createVNode(DataGroupChooseShareModal, {
      ref_key: "dataGroupChooseModalRef",
      ref: dataGroupChooseModalRef,
      onRequestShare: onRequestShare
    }, null, 512),
    _createVNode(Modal, {
      ref_key: "removeResearchGroupModalRef",
      ref: removeResearchGroupModalRef,
      "ok-style": "danger",
      onOk: removeResearchGroup
    }, {
      title: _withCtx(() => [
        _createTextVNode("Stop sharing")
      ]),
      okText: _withCtx(() => [
        _createTextVNode("Stop sharing")
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_10, [
          _hoisted_11,
          _createVNode(ResearchGroupItem, { "data-group-id": removeDataGroupIdRef.value }, null, 8, ["data-group-id"])
        ])
      ]),
      _: 1
    }, 512)
  ]))
}
}

})